import activeCategoriesQuery from '../getActiveCategories.graphql';
import homeSubCategoriesQuery from './getHomeSubCategories.graphql';
import { encode } from '../../../helpers/queryEncryption';
import { TABS, TAB_CATEGORY_IDS, MAX_TASKS_PER_TAB, CHIPS, PROJECTS } from './content';

// Booking entry for a category: the same encrypted-id URL every working category link uses.
// A plain numeric `type` is rejected by the /category route, so always go through encode().
export const categoryHref = (id) => `/category?type=${encodeURIComponent(encode(String(id)))}`;

// Display tidy-up of admin-entered names: "standard  Cleaning" → "Standard Cleaning".
const tidyName = (name) => {
  const t = String(name || '').replace(/\s+/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

const orderSubCategories = (subs, prefer = []) => {
  const rank = (s) => {
    const i = prefer.indexOf(s.id);
    return i === -1 ? prefer.length + s.id : i;
  };
  return subs.slice().sort((a, b) => rank(a) - rank(b));
};

// Builds one tab from live data: tasks are real sub-categories of the tab's categories, each
// linking to its own category booking page. Returns null when none of its categories exist.
function buildTab(tab, categoriesById, subsByCategory) {
  const present = tab.categories.filter((c) => categoriesById[c.id]);
  if (!present.length) return null;

  const pools = present.map((c) => ({
    ...c,
    queue: orderSubCategories(subsByCategory[c.id] || [], c.prefer),
  }));
  const tasks = [];
  const take = (pool, limit) => {
    while (pool.queue.length && tasks.length < MAX_TASKS_PER_TAB && limit > 0) {
      const sub = pool.queue.shift();
      const name = tidyName(sub.name);
      if (name) {
        tasks.push({ key: `s${sub.id}`, label: name, categoryId: pool.id });
        limit -= 1;
      }
    }
  };
  // Pass 1 respects each category's `max`; pass 2 fills the remaining slots in order.
  pools.forEach((p) => take(p, p.max || MAX_TASKS_PER_TAB));
  pools.forEach((p) => take(p, MAX_TASKS_PER_TAB));
  // A category with no active sub-category is still bookable: offer the category itself.
  if (!tasks.length) {
    present.forEach((c) => tasks.push({ key: `c${c.id}`, label: tidyName(categoriesById[c.id].name), categoryId: c.id }));
  }

  const main = categoriesById[present[0].id];
  return {
    key: tab.key,
    label: tab.label,
    icon: tab.icon,
    tasks,
    seeAllHref: main.seoSlug ? `/services/${main.seoSlug}` : categoryHref(main.id),
    seeAllLabel: `See all ${tab.label.toLowerCase()} services`,
  };
}

// Loads everything the new homepage needs through the existing public GraphQL queries
// (getActiveCategories + getSubCategories). Any failure degrades to fewer items, never a crash.
// Default fetch policy (cache-first): the client's first render reuses the SSR Apollo cache.
export default async function loadHomeV2Data(client) {
  let categories = [];
  try {
    const { data } = await client.query({ query: activeCategoriesQuery });
    categories = (data && data.getActiveCategories && data.getActiveCategories.results) || [];
  } catch (e) {
    categories = [];
  }
  const categoriesById = {};
  categories.forEach((c) => { if (c && c.id != null) categoriesById[c.id] = c; });

  const subsByCategory = {};
  await Promise.all(TAB_CATEGORY_IDS.filter((id) => categoriesById[id]).map(async (id) => {
    try {
      const { data } = await client.query({ query: homeSubCategoriesQuery, variables: { categoryId: id } });
      const r = data && data.getSubCategories;
      subsByCategory[id] = (r && r.status === 200 && r.results) || [];
    } catch (e) {
      subsByCategory[id] = [];
    }
  }));

  return {
    tabs: TABS.map((t) => buildTab(t, categoriesById, subsByCategory)).filter(Boolean),
    chips: CHIPS.filter((c) => categoriesById[c.categoryId]),
    projects: PROJECTS.filter((p) => categoriesById[p.categoryId]),
  };
}
